import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { addSurfaceRestPositionAttribute } from '../services/fracture/cellGeometry';
import {
  getSurface,
  getSurfaceBundle,
  listSurfaces,
  type SurfaceFace,
} from '../materials/MaterialRegistry';
import { hashSettings, type SurfaceControlSpec } from '../materials/core/SurfaceDefinition';
import {
  bakeSurfaceMaps,
  copyBakedMapsToCanvas,
  createBakedPreviewMaterials,
  disposeBakedPreviewMaterials,
  type BakedSurfaceMaps,
} from '../materials/core/SurfaceBaker';
import {
  BAKE_FRONT_SIZE,
  BAKE_SIDE_SIZE,
  createBakedSurfaceMaterial,
  type BakedSurfaceMaterialHandle,
} from '../materials/core/BakedSurfaceMaterial';
import type { ProfileId } from '../services/destructionProfiles';

interface BeautyShotHandle {
  samples: number;
  done: boolean;
  targetSamples: number;
  renderSample: () => void;
  savePng: () => Promise<Blob>;
  dispose: () => void;
}
import {
  advanceTextureLabDrop,
  clearTextureLabDrops,
  createTextureLabDropWorld,
  disposeTextureLabDropWorld,
  setTextureLabWallCollider,
  spawnTextureLabDrops,
  TEXLAB_BOX_HALF,
  TEXLAB_DROP_COUNT,
  type TextureLabDropRuntime,
} from '../structures/textureLab/textureLabPhysics';
import './TextureLab.css';

type LightingKind = 'dusk' | 'studio' | 'gallery';
type CameraKind = 'wide' | 'mid' | 'close';

const LAB_SURFACES = listSurfaces();
const SURFACE_IDS = LAB_SURFACES.map((s) => s.id);

const CAMERAS: Record<CameraKind, { pos: [number, number, number]; target: [number, number, number]; fov: number }> = {
  wide: { pos: [7.2, 3.6, 8.2], target: [0, 2.2, 0], fov: 45 },
  mid: { pos: [4.2, 2.6, 5.4], target: [0, 2.15, 0], fov: 42 },
  close: { pos: [1.2, 2.15, 2.7], target: [0, 2.15, 0], fov: 38 },
};

const PEDESTAL_H = 0.16;
const EXPOSURE: Record<LightingKind, number> = { dusk: 1.12, studio: 1.0, gallery: 1.05 };

interface BeautyShotBridge {
  active: boolean;
  samples: number;
  done: boolean;
}

interface TextureLabState {
  ready: boolean;
  surface: string;
  face: SurfaceFace;
  lighting: LightingKind;
  settingsHash: string;
  dropBodies: number;
  fps: number;
  dropAwake: number;
  simTime: number;
  bakePreview: boolean;
  bakedActive: boolean;
  bakeFailed: boolean;
  bakeMs: number;
  textureCount: number;
  bakeSeq: number;
  beautyShot: BeautyShotBridge;
}

interface TextureLabProbe {
  setSurface: (id: string) => void;
  setFace: (face: SurfaceFace) => void;
  setLighting: (kind: LightingKind) => void;
  setCamera: (px: number, py: number, pz: number, tx?: number, ty?: number, tz?: number, fov?: number) => void;
  getCamera: () => { x: number; y: number; z: number; tx: number; ty: number; tz: number } | null;
  drop: () => void;
  clearDrop: () => void;
  setControl: (key: string, value: number | string | boolean) => void;
  getSettings: () => unknown;
  resetSettings: () => void;
  setBakePreview: (on: boolean) => void;
  startBeautyShot: (opts?: { samples?: number; height?: number; bakeSize?: number; rasterOnly?: boolean }) => void;
  stopBeautyShot: () => void;
  saveBeautyPng: () => void;
}

declare global {
  interface Window {
    __textureLabState?: TextureLabState;
    __textureLabProbe?: TextureLabProbe;
  }
}

function writeState(patch: Partial<TextureLabState>): void {
  const prev = window.__textureLabState;
  window.__textureLabState = {
    ready: patch.ready ?? prev?.ready ?? false,
    surface: patch.surface ?? prev?.surface ?? 'concrete',
    face: patch.face ?? prev?.face ?? 'exterior',
    lighting: patch.lighting ?? prev?.lighting ?? 'dusk',
    settingsHash: patch.settingsHash ?? prev?.settingsHash ?? '',
    dropBodies: patch.dropBodies ?? prev?.dropBodies ?? 0,
    fps: patch.fps ?? prev?.fps ?? 0,
    dropAwake: patch.dropAwake ?? prev?.dropAwake ?? 0,
    simTime: patch.simTime ?? prev?.simTime ?? 0,
    bakePreview: patch.bakePreview ?? prev?.bakePreview ?? false,
    bakedActive: patch.bakedActive ?? prev?.bakedActive ?? false,
    bakeFailed: patch.bakeFailed ?? prev?.bakeFailed ?? false,
    bakeMs: patch.bakeMs ?? prev?.bakeMs ?? 0,
    textureCount: patch.textureCount ?? prev?.textureCount ?? 0,
    bakeSeq: patch.bakeSeq ?? prev?.bakeSeq ?? 0,
    beautyShot: patch.beautyShot ?? prev?.beautyShot ?? { active: false, samples: 0, done: false },
  };
  if (window.__textureLabState.ready) labUiMarkReady();
}

// Loading-indicator store. Deliberately separate from the window bridge: fps writes
// hit the bridge every frame, and the overlay must not re-render the page per frame.
type LabUiSnapshot = { ready: boolean; busy: boolean };
let labUiSnapshot: LabUiSnapshot = { ready: false, busy: false };
let labBusyCount = 0;
const labUiListeners = new Set<() => void>();
function labUiSet(ready: boolean, busy: boolean): void {
  if (import.meta.env.DEV) {
    (window as unknown as { __labBusyCount?: number }).__labBusyCount = labBusyCount;
  }
  if (labUiSnapshot.ready === ready && labUiSnapshot.busy === busy) return;
  labUiSnapshot = { ready, busy };
  for (const listener of labUiListeners) listener();
}
function labUiMarkReady(): void {
  labUiSet(true, labUiSnapshot.busy);
}
function labUiBeginBusy(): void {
  labBusyCount += 1;
  labUiSet(labUiSnapshot.ready, true);
}
function labUiEndBusy(): void {
  labBusyCount = Math.max(0, labBusyCount - 1);
  labUiSet(labUiSnapshot.ready, labBusyCount > 0);
}
function labUiReset(): void {
  labBusyCount = 0;
  labUiSet(false, false);
}
function useLabUi(): LabUiSnapshot {
  return useSyncExternalStore(
    (onChange) => {
      labUiListeners.add(onChange);
      return () => labUiListeners.delete(onChange);
    },
    () => labUiSnapshot,
  );
}

const BAKE_DEBOUNCE_MS = 200;
const CAMERA_IDLE_POS2 = 1e-10;
const CAMERA_IDLE_ANGLE = 1e-5;

function CameraDampingCutoff({
  controlsRef,
  resetRef,
}: {
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  resetRef: MutableRefObject<boolean>;
}) {
  const { camera } = useThree();
  const prevPos = useRef(new THREE.Vector3());
  const prevTarget = useRef(new THREE.Vector3());
  const prevQuat = useRef(new THREE.Quaternion());
  const primed = useRef(false);

  useFrame(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    if (resetRef.current || !primed.current) {
      prevPos.current.copy(camera.position);
      prevTarget.current.copy(controls.target);
      prevQuat.current.copy(camera.quaternion);
      primed.current = true;
      resetRef.current = false;
      return;
    }
    const posD2 = camera.position.distanceToSquared(prevPos.current);
    const tgtD2 = controls.target.distanceToSquared(prevTarget.current);
    const qDot = Math.min(1, Math.abs(camera.quaternion.dot(prevQuat.current)));
    const angle = 2 * Math.acos(qDot);
    if (posD2 < CAMERA_IDLE_POS2 && tgtD2 < CAMERA_IDLE_POS2 && angle < CAMERA_IDLE_ANGLE) {
      camera.position.copy(prevPos.current);
      controls.target.copy(prevTarget.current);
      camera.quaternion.copy(prevQuat.current);
      camera.updateMatrixWorld();
    } else {
      prevPos.current.copy(camera.position);
      prevTarget.current.copy(controls.target);
      prevQuat.current.copy(camera.quaternion);
    }
  });

  return null;
}

function initialSurfaceId(): ProfileId {
  const param = new URLSearchParams(window.location.search).get('surface');
  if (param && SURFACE_IDS.includes(param as ProfileId)) return param as ProfileId;
  return 'concrete';
}

function cloneSettings<T>(settings: T): T {
  return JSON.parse(JSON.stringify(settings)) as T;
}

function applyCamera(
  camera: THREE.Camera,
  controls: OrbitControlsImpl | null,
  spec: { pos: [number, number, number]; target: [number, number, number]; fov: number },
): void {
  camera.position.set(...spec.pos);
  if (camera instanceof THREE.PerspectiveCamera) {
    camera.fov = spec.fov;
    camera.updateProjectionMatrix();
  }
  camera.lookAt(...spec.target);
  if (controls) {
    controls.target.set(...spec.target);
    controls.update();
  }
}

function LightingHost({
  lightingRef,
  dirtyRef,
}: {
  lightingRef: MutableRefObject<LightingKind>;
  dirtyRef: MutableRefObject<boolean>;
}) {
  const { gl, scene } = useThree();
  const envRef = useRef<{ pmrem: THREE.PMREMGenerator; texture: THREE.Texture } | null>(null);
  const lightsRef = useRef<THREE.Object3D[]>([]);
  const applied = useRef<LightingKind | null>(null);

  const clearLights = useCallback(() => {
    for (const obj of lightsRef.current) {
      scene.remove(obj);
      const light = obj as THREE.Light;
      if (typeof light.dispose === 'function') light.dispose();
    }
    lightsRef.current = [];
  }, [scene]);

  const apply = useCallback(
    (kind: LightingKind) => {
      gl.toneMapping = THREE.AgXToneMapping;
      gl.toneMappingExposure = EXPOSURE[kind];
      writeState({ lighting: kind });
      gl.shadowMap.enabled = kind !== 'studio';
      gl.shadowMap.type = THREE.PCFSoftShadowMap;

      if (!envRef.current) {
        const pmrem = new THREE.PMREMGenerator(gl);
        const room = new RoomEnvironment();
        const envRT = pmrem.fromScene(room, 0.04);
        room.dispose();
        envRef.current = { pmrem, texture: envRT.texture };
      }
      scene.environment = envRef.current.texture;
      clearLights();

      const add = (obj: THREE.Object3D) => {
        scene.add(obj);
        lightsRef.current.push(obj);
      };

      if (kind === 'dusk') {
        scene.background = new THREE.Color('#1a140f');
        scene.fog = new THREE.FogExp2('#c9925e', 0.0082);
        scene.environmentIntensity = 0.34;
        const hemi = new THREE.HemisphereLight(0x4a5680, 0x6e4a30, 0.58);
        add(hemi);
        const amb = new THREE.AmbientLight(0x3a2c22, 0.04);
        add(amb);
        const key = new THREE.DirectionalLight(0xffd2a8, 2.55);
        key.position.set(-10, 10.5, 8);
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        key.shadow.camera.left = -12;
        key.shadow.camera.right = 12;
        key.shadow.camera.top = 12;
        key.shadow.camera.bottom = -6;
        key.shadow.camera.near = 2;
        key.shadow.camera.far = 42;
        key.shadow.bias = -0.00022;
        key.shadow.normalBias = 0.04;
        add(key);
        const rim = new THREE.DirectionalLight(0x97a8d4, 2.05);
        rim.position.set(1.2, 14.5, -13.5);
        add(rim);
      } else if (kind === 'studio') {
        scene.background = new THREE.Color('#1c1e22');
        scene.fog = null;
        scene.environmentIntensity = 1.05;
        const amb = new THREE.AmbientLight(0xd8dce2, 0.28);
        add(amb);
        const fill = new THREE.DirectionalLight(0xf2f4f7, 0.35);
        fill.position.set(4, 8, 6);
        add(fill);
      } else {
        scene.background = new THREE.Color('#090b0d');
        scene.fog = null;
        // Dim IBL/hemi so the raking key can actually describe normals at close range.
        scene.environmentIntensity = 0.10;
        const hemi = new THREE.HemisphereLight(0x9fb9c8, 0x090b0d, 0.28);
        add(hemi);
        const key = new THREE.DirectionalLight(0xbfd9e8, 0.42);
        key.position.set(-8, 5.5, 3.5);
        key.castShadow = true;
        key.shadow.mapSize.set(1024, 1024);
        key.shadow.camera.left = -10;
        key.shadow.camera.right = 10;
        key.shadow.camera.top = 10;
        key.shadow.camera.bottom = -4;
        key.shadow.camera.near = 1;
        key.shadow.camera.far = 36;
        key.shadow.bias = -0.00018;
        key.shadow.normalBias = 0.035;
        add(key);
        const amber = new THREE.PointLight(0xffb060, 10, 6.5, 2);
        amber.position.set(-2.6, 1.6, 1.8);
        add(amber);
        const cyan = new THREE.PointLight(0x70d4e8, 8, 6.5, 2);
        cyan.position.set(2.8, 1.5, 1.6);
        add(cyan);
        // Grazing raking spot: just in front of the wall, firing along +X so
        // close-range height/normal detail reads. Previous spot sat above-front
        // and flattened everything into a fill.
        const spot = new THREE.SpotLight(0xf4f7ff, 64, 14, Math.PI / 7.5, 0.38, 1.15);
        spot.position.set(-4.6, 2.25, 0.62);
        spot.target.position.set(1.4, 2.05, 0.16);
        spot.castShadow = true;
        spot.shadow.bias = -0.00012;
        spot.shadow.normalBias = 0.03;
        add(spot);
        add(spot.target);
        const rake = new THREE.DirectionalLight(0xe8eef6, 1.15);
        rake.position.set(6.5, 1.8, 0.9);
        add(rake);
      }
      applied.current = kind;
      dirtyRef.current = false;
    },
    [clearLights, dirtyRef, gl, scene],
  );

  useEffect(() => {
    apply(lightingRef.current);
    return () => {
      clearLights();
      if (envRef.current) {
        envRef.current.texture.dispose();
        envRef.current.pmrem.dispose();
        envRef.current = null;
      }
      scene.environment = null;
      scene.fog = null;
    };
  }, [apply, clearLights, lightingRef, scene]);

  useFrame(() => {
    if (dirtyRef.current || applied.current !== lightingRef.current) {
      apply(lightingRef.current);
    }
  });

  return null;
}

type BeautyCmd = {
  kind: 'start' | 'stop' | 'save';
  samples?: number;
  height?: number;
  bakeSize?: number;
  rasterOnly?: boolean;
} | null;

function LabScene({
  surfaceIdRef,
  faceRef,
  settingsRef,
  lightingRef,
  lightingDirtyRef,
  dropCmdRef,
  controlsRef,
  cameraCmdRef,
  bakePreviewRef,
  beautyCmdRef,
  cameraSnapResetRef,
}: {
  surfaceIdRef: MutableRefObject<ProfileId>;
  faceRef: MutableRefObject<SurfaceFace>;
  settingsRef: MutableRefObject<unknown>;
  lightingRef: MutableRefObject<LightingKind>;
  lightingDirtyRef: MutableRefObject<boolean>;
  dropCmdRef: MutableRefObject<'spawn' | 'clear' | null>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  cameraCmdRef: MutableRefObject<CameraKind | null>;
  bakePreviewRef: MutableRefObject<boolean>;
  beautyCmdRef: MutableRefObject<BeautyCmd>;
  cameraSnapResetRef: MutableRefObject<boolean>;
}) {
  const { scene, camera, gl } = useThree();
  const wallRef = useRef<THREE.Mesh | null>(null);
  const dropMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const materialRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const physicsRef = useRef<TextureLabDropRuntime | null>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const fpsEma = useRef(60);
  const applied = useRef({ surface: '', face: '' as SurfaceFace | '', hash: '' });
  const bakedRef = useRef<{ key: string; maps: BakedSurfaceMaps } | null>(null);
  const bakedMatsRef = useRef<THREE.MeshPhysicalMaterial[] | null>(null);
  const bakedHandleRef = useRef<BakedSurfaceMaterialHandle | null>(null);
  const canvasMapsRef = useRef<BakedSurfaceMaps | null>(null);
  const bakeTimerRef = useRef<number>(0);
  const bakeSeqRef = useRef(0);
  const beautyRef = useRef<BeautyShotHandle | null>(null);
  const beautyLoadingRef = useRef(false);
  const appliedBakePreview = useRef(true);
  const origRenderRef = useRef<THREE.WebGLRenderer['render'] | null>(null);
  const appliedBakedKey = useRef('');

  const textureCount = useCallback(() => gl.info.memory.textures, [gl]);

  const wallSpec = useCallback(() => {
    const bundle = getSurfaceBundle(surfaceIdRef.current);
    const wall = bundle?.physics.wall ?? { w: 6, h: 4.2, t: 0.3 };
    return {
      width: wall.w,
      height: wall.h,
      thickness: wall.t,
      position: new THREE.Vector3(0, PEDESTAL_H + wall.h / 2, 0),
    };
  }, [surfaceIdRef]);

  const setBeautyRenderPatch = useCallback(
    (on: boolean) => {
      if (on) {
        if (!origRenderRef.current) origRenderRef.current = gl.render.bind(gl);
        const orig = origRenderRef.current;
        const r3fScene = scene;
        gl.render = ((s, c) => {
          if (beautyRef.current && s === r3fScene) return;
          orig(s, c);
        }) as THREE.WebGLRenderer['render'];
      } else if (origRenderRef.current) {
        gl.render = origRenderRef.current;
      }
    },
    [gl, scene],
  );

  const hideBakedMeshes = useCallback(() => {
    if (canvasMapsRef.current) {
      canvasMapsRef.current.dispose();
      canvasMapsRef.current = null;
    }
    if (bakedHandleRef.current) {
      bakedHandleRef.current.dispose();
      bakedHandleRef.current = null;
    }
    appliedBakedKey.current = '';
  }, []);

  const rebuildWall = useCallback(() => {
    const id = surfaceIdRef.current;
    const bundle = getSurfaceBundle(id);
    const def = bundle?.definition ?? getSurface(id);
    if (!def || !bundle) return;
    const face = faceRef.current;
    const settings = settingsRef.current ?? def.defaultSettings;
    const skin = bundle.physics.skin;
    const params = face === 'interior' ? skin.interior : skin.outer;
    const nextMat = def.createMaterial(params, settings, face);

    if (materialRef.current) {
      materialRef.current.dispose();
    }
    materialRef.current = nextMat;

    const wall = bundle.physics.wall;
    const geom = new THREE.BoxGeometry(wall.w, wall.h, wall.t);
    addSurfaceRestPositionAttribute(geom);
    if (wallRef.current) {
      wallRef.current.geometry.dispose();
      wallRef.current.geometry = geom;
      wallRef.current.material = nextMat;
      wallRef.current.position.set(0, PEDESTAL_H + wall.h / 2, 0);
      wallRef.current.visible = false;
    } else {
      const mesh = new THREE.Mesh(geom, nextMat);
      mesh.name = 'texture-lab-wall';
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.visible = false;
      mesh.position.set(0, PEDESTAL_H + wall.h / 2, 0);
      scene.add(mesh);
      wallRef.current = mesh;
    }

    if (dropMeshRef.current) {
      dropMeshRef.current.material = nextMat;
    }

    const phys = physicsRef.current;
    if (phys) setTextureLabWallCollider(phys, wall, PEDESTAL_H);

    applied.current = { surface: id, face, hash: hashSettings(settings) };
    writeState({ surface: id, face, settingsHash: applied.current.hash });
  }, [faceRef, scene, settingsRef, surfaceIdRef]);

  const disposeBaked = useCallback(() => {
    if (wallRef.current && materialRef.current) {
      wallRef.current.material = materialRef.current;
      wallRef.current.visible = true;
    }
    if (bakedMatsRef.current) {
      disposeBakedPreviewMaterials(bakedMatsRef.current);
      bakedMatsRef.current = null;
    }
    bakedRef.current?.maps.dispose();
    bakedRef.current = null;
    hideBakedMeshes();
    appliedBakePreview.current = false;
  }, [hideBakedMeshes]);

  const applyBakedWall = useCallback(() => {
    const id = surfaceIdRef.current;
    const bundle = getSurfaceBundle(id);
    const def = bundle?.definition ?? getSurface(id);
    if (!def || !bundle) return;
    const face = faceRef.current;
    const settings = settingsRef.current ?? def.defaultSettings;
    const spec = wallSpec();
    const wallKey = `${spec.width}|${spec.height}|${spec.thickness}`;
    const key = `${id}|${face}|${hashSettings(settings)}|${wallKey}`;
    if (bakedHandleRef.current && appliedBakedKey.current === key) {
      writeState({
        bakedActive: true,
        bakeFailed: false,
        bakePreview: bakePreviewRef.current,
        textureCount: textureCount(),
        ready: true,
      });
      return;
    }

    const canRebake =
      !!bakedHandleRef.current &&
      appliedBakedKey.current.startsWith(`${id}|${face}|`) &&
      appliedBakedKey.current.endsWith(`|${wallKey}`);

    if (canRebake && bakedHandleRef.current) {
      if (canvasMapsRef.current) {
        canvasMapsRef.current.dispose();
        canvasMapsRef.current = null;
      }
      bakedHandleRef.current.rebake(settings);
      appliedBakedKey.current = key;
      bakeSeqRef.current += 1;
      if (wallRef.current) wallRef.current.visible = false;
      applied.current = { surface: id, face, hash: hashSettings(settings) };
      writeState({
        surface: id,
        face,
        settingsHash: applied.current.hash,
        bakedActive: true,
        bakeFailed: false,
        bakeMs: bakedHandleRef.current.bakeMs,
        textureCount: textureCount(),
        bakeSeq: bakeSeqRef.current,
        bakePreview: bakePreviewRef.current,
        ready: true,
      });
      if (dropMeshRef.current) dropMeshRef.current.material = bakedHandleRef.current.materials[4];
      return;
    }

    if (!wallRef.current || !appliedBakedKey.current.startsWith(`${id}|${face}|`)) {
      rebuildWall();
    }
    if (wallRef.current) wallRef.current.visible = false;
    const handle = createBakedSurfaceMaterial(gl, def, settings, face, {
      wall: spec,
      frontSize: BAKE_FRONT_SIZE,
      sideSize: BAKE_SIDE_SIZE,
    });
    for (const mesh of handle.meshes) {
      mesh.name = `texture-lab-wall-face`;
      scene.add(mesh);
    }
    const prevHandle = bakedHandleRef.current;
    bakedHandleRef.current = handle;
    appliedBakedKey.current = key;
    if (prevHandle) prevHandle.dispose();
    if (canvasMapsRef.current) {
      canvasMapsRef.current.dispose();
      canvasMapsRef.current = null;
    }
    bakeSeqRef.current += 1;
    if (wallRef.current) wallRef.current.visible = false;
    applied.current = { surface: id, face, hash: hashSettings(settings) };
    writeState({
      surface: id,
      face,
      settingsHash: applied.current.hash,
      bakedActive: true,
      bakeFailed: false,
      bakeMs: handle.bakeMs,
      textureCount: textureCount(),
      bakeSeq: bakeSeqRef.current,
      bakePreview: bakePreviewRef.current,
      ready: true,
    });
    if (dropMeshRef.current) dropMeshRef.current.material = handle.materials[4];
  }, [
    bakePreviewRef,
    faceRef,
    gl,
    rebuildWall,
    scene,
    settingsRef,
    surfaceIdRef,
    textureCount,
    wallSpec,
  ]);

  const ensureBaked = useCallback(
    (size: number): BakedSurfaceMaps | null => {
      if (bakedHandleRef.current) {
        const live = bakedHandleRef.current.maps;
        if (live.targets.length === 0) return live;
        if (!canvasMapsRef.current) canvasMapsRef.current = copyBakedMapsToCanvas(gl, live);
        return canvasMapsRef.current;
      }
      const wall = wallRef.current;
      const def = getSurface(surfaceIdRef.current);
      const bundle = getSurfaceBundle(surfaceIdRef.current);
      if (!wall || !def || !bundle) return null;
      const key = `${surfaceIdRef.current}|${faceRef.current}|${hashSettings(settingsRef.current)}|${size}`;
      if (bakedRef.current?.key === key) return bakedRef.current.maps;
      if (bakedMatsRef.current) {
        disposeBakedPreviewMaterials(bakedMatsRef.current);
        bakedMatsRef.current = null;
      }
      bakedRef.current?.maps.dispose();
      bakedRef.current = null;
      const maps = bakeSurfaceMaps(gl, def, settingsRef.current ?? def.defaultSettings, faceRef.current, size, {
        width: bundle.physics.wall.w,
        height: bundle.physics.wall.h,
        thickness: bundle.physics.wall.t,
        position: wall.position.clone(),
        mesh: wall,
      });
      const settings = settingsRef.current as { clearcoat?: number; clearcoatRoughness?: number } | null;
      const mats = createBakedPreviewMaterials(maps, {
        clearcoat: settings?.clearcoat ?? 0,
        clearcoatRoughness: settings?.clearcoatRoughness ?? 1,
      });
      if (!wall.geometry.getAttribute('tangent')) {
        wall.geometry.computeTangents();
      }
      bakedRef.current = { key, maps };
      bakedMatsRef.current = mats;
      return maps;
    },
    [faceRef, gl, settingsRef, surfaceIdRef],
  );

  const bakeGenRef = useRef(0);
  const scheduleBake = useCallback(
    (immediate: boolean) => {
      if (bakeTimerRef.current) {
        window.clearTimeout(bakeTimerRef.current);
        bakeTimerRef.current = 0;
      }
      const gen = ++bakeGenRef.current;
      const run = () => {
        bakeTimerRef.current = 0;
        // Bakes are synchronous on the main thread; show the busy chip and give the
        // browser two rAFs to paint it before blocking. The spinner animation is
        // compositor-driven (transform), so it keeps turning through the bake.
        labUiBeginBusy();
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (gen !== bakeGenRef.current) {
              labUiEndBusy();
              return;
            }
            try {
              applyBakedWall();
              appliedBakePreview.current = bakePreviewRef.current;
            } catch (err) {
              console.error('[texture-lab] bake failed', err);
              if (wallRef.current) wallRef.current.visible = false;
              hideBakedMeshes();
              writeState({
                bakedActive: false,
                bakeFailed: true,
                ready: true,
                textureCount: textureCount(),
              });
            } finally {
              labUiEndBusy();
            }
          }),
        );
      };
      if (immediate) run();
      else bakeTimerRef.current = window.setTimeout(run, BAKE_DEBOUNCE_MS);
    },
    [applyBakedWall, bakePreviewRef, hideBakedMeshes, textureCount],
  );

  useEffect(() => {
    const groundMat = new THREE.MeshStandardMaterial({ color: '#3d433c', roughness: 0.92, metalness: 0.02 });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(48, 48), groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    ground.name = 'texture-lab-ground';
    scene.add(ground);

    const pedestalMat = new THREE.MeshStandardMaterial({ color: '#2a2c2e', roughness: 0.78, metalness: 0.04 });
    const pedestal = new THREE.Mesh(new THREE.BoxGeometry(6.8, PEDESTAL_H, 1.35), pedestalMat);
    pedestal.position.set(0, PEDESTAL_H / 2, 0);
    pedestal.receiveShadow = true;
    pedestal.castShadow = true;
    pedestal.name = 'texture-lab-pedestal';
    scene.add(pedestal);

    const dropGeom = new THREE.BoxGeometry(TEXLAB_BOX_HALF * 2, TEXLAB_BOX_HALF * 2, TEXLAB_BOX_HALF * 2);
    addSurfaceRestPositionAttribute(dropGeom);
    dropGeom.computeTangents();
    const dropMesh = new THREE.InstancedMesh(dropGeom, new THREE.MeshStandardMaterial({ color: '#888' }), TEXLAB_DROP_COUNT);
    dropMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    dropMesh.castShadow = true;
    dropMesh.receiveShadow = true;
    dropMesh.frustumCulled = false;
    dropMesh.count = 0;
    dropMesh.name = 'texture-lab-drops';
    scene.add(dropMesh);
    dropMeshRef.current = dropMesh;

    rebuildWall();
    scheduleBake(true);

    let cancelled = false;
    void createTextureLabDropWorld().then((rt) => {
      if (cancelled) {
        disposeTextureLabDropWorld(rt);
        return;
      }
      physicsRef.current = rt;
      const bundle = getSurfaceBundle(surfaceIdRef.current);
      if (bundle) setTextureLabWallCollider(rt, bundle.physics.wall, PEDESTAL_H);
    });

    return () => {
      cancelled = true;
      scene.remove(ground);
      scene.remove(pedestal);
      ground.geometry.dispose();
      groundMat.dispose();
      pedestal.geometry.dispose();
      pedestalMat.dispose();
      if (wallRef.current) {
        scene.remove(wallRef.current);
        wallRef.current.geometry.dispose();
        wallRef.current = null;
      }
      if (dropMeshRef.current) {
        scene.remove(dropMeshRef.current);
        dropMeshRef.current.geometry.dispose();
        dropMeshRef.current = null;
      }
      materialRef.current?.dispose();
      materialRef.current = null;
      beautyRef.current?.dispose();
      beautyRef.current = null;
      setBeautyRenderPatch(false);
      if (bakeTimerRef.current) {
        window.clearTimeout(bakeTimerRef.current);
        bakeTimerRef.current = 0;
      }
      bakeGenRef.current += 1;
      disposeBaked();
      if (physicsRef.current) {
        disposeTextureLabDropWorld(physicsRef.current);
        physicsRef.current = null;
      }
    };
  }, [disposeBaked, rebuildWall, scene, scheduleBake, setBeautyRenderPatch, surfaceIdRef]);

  useFrame((_, delta) => {
    const beautyCmd = beautyCmdRef.current;
    if (beautyCmd) {
      beautyCmdRef.current = null;
      if (beautyCmd.kind === 'stop') {
        beautyRef.current?.dispose();
        beautyRef.current = null;
        setBeautyRenderPatch(false);
        if (controlsRef.current) controlsRef.current.enabled = true;
        writeState({ beautyShot: { active: false, samples: 0, done: false } });
      } else if (beautyCmd.kind === 'save') {
        void beautyRef.current?.savePng().then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${surfaceIdRef.current}-beauty.png`;
          a.click();
          URL.revokeObjectURL(url);
        });
      } else if (beautyCmd.kind === 'start' && !beautyLoadingRef.current && !beautyRef.current) {
        const wall = wallRef.current;
        const bundle = getSurfaceBundle(surfaceIdRef.current);
        if (wall && bundle) {
          beautyLoadingRef.current = true;
          writeState({ beautyShot: { active: true, samples: 0, done: false } });
          if (controlsRef.current) controlsRef.current.enabled = false;
          const bakeSize = beautyCmd.bakeSize ?? 1024;
          const targetSamples = beautyCmd.samples ?? 512;
          const height = beautyCmd.height;
          const rasterOnly = beautyCmd.rasterOnly === true;
          const lighting = lightingRef.current;
          const settings = settingsRef.current as { clearcoat?: number; clearcoatRoughness?: number } | null;
          const maps = ensureBaked(bakeSize);
          if (!maps) {
            beautyLoadingRef.current = false;
            if (controlsRef.current) controlsRef.current.enabled = true;
            writeState({ beautyShot: { active: false, samples: 0, done: false } });
          } else {
          void import('./textureLabBeauty')
            .then((mod) => {
              const handle = mod.startBeautyShot({
                renderer: gl,
                camera,
                baked: maps,
                wall: {
                  width: bundle.physics.wall.w,
                  height: bundle.physics.wall.h,
                  thickness: bundle.physics.wall.t,
                  position: wall.position.clone(),
                },
                lighting,
                exposure: EXPOSURE[lighting],
                targetSamples,
                renderHeight: height,
                rasterOnly,
                clearcoat: settings?.clearcoat ?? 0,
                clearcoatRoughness: settings?.clearcoatRoughness ?? 1,
              });
              beautyRef.current = handle;
              setBeautyRenderPatch(true);
              handle.renderSample();
              writeState({
                beautyShot: { active: true, samples: handle.samples, done: handle.done },
              });
            })
            .catch((err: unknown) => {
              console.error('beauty shot failed', err);
              setBeautyRenderPatch(false);
              if (controlsRef.current) controlsRef.current.enabled = true;
              writeState({ beautyShot: { active: false, samples: 0, done: false } });
            })
            .finally(() => {
              beautyLoadingRef.current = false;
            });
          }
        }
      }
    }

    const beauty = beautyRef.current;
    if (beauty) {
      beauty.renderSample();
      writeState({
        beautyShot: { active: true, samples: beauty.samples, done: beauty.done },
      });
    } else if (bakePreviewRef.current !== appliedBakePreview.current) {
      appliedBakePreview.current = bakePreviewRef.current;
      scheduleBake(true);
    }

    if (cameraCmdRef.current && !beauty) {
      applyCamera(camera, controlsRef.current, CAMERAS[cameraCmdRef.current]);
      cameraSnapResetRef.current = true;
      cameraCmdRef.current = null;
    }

    const hash = hashSettings(settingsRef.current);
    if (!beauty) {
      if (
        applied.current.surface !== surfaceIdRef.current ||
        applied.current.face !== faceRef.current
      ) {
        scheduleBake(false);
        applied.current = {
          surface: surfaceIdRef.current,
          face: faceRef.current,
          hash,
        };
        writeState({
          surface: surfaceIdRef.current,
          face: faceRef.current,
          settingsHash: hash,
        });
      } else if (applied.current.hash !== hash && materialRef.current) {
        const def = getSurface(surfaceIdRef.current);
        def?.updateMaterial(materialRef.current, settingsRef.current);
        applied.current.hash = hash;
        writeState({ settingsHash: hash });
        scheduleBake(false);
      }
    }

    const cmd = dropCmdRef.current;
    const phys = physicsRef.current;
    if (cmd && phys) {
      if (cmd === 'clear') {
        clearTextureLabDrops(phys);
        if (dropMeshRef.current) dropMeshRef.current.count = 0;
      } else {
        const bundle = getSurfaceBundle(surfaceIdRef.current);
        const seed = Number((settingsRef.current as { seed?: number } | null)?.seed) || 17;
        spawnTextureLabDrops(phys, {
          density: bundle?.physics.density ?? 1200,
          friction: bundle?.physics.surface.friction ?? 0.8,
          restitution: bundle?.physics.surface.restitution ?? 0.05,
          seed,
        });
        if (dropMeshRef.current) dropMeshRef.current.count = TEXLAB_DROP_COUNT;
      }
      dropCmdRef.current = null;
    }

    if (phys && phys.dropBodies.length > 0) {
      advanceTextureLabDrop(phys, Math.min(delta, 0.05));
      const mesh = dropMeshRef.current;
      if (mesh) {
        for (let i = 0; i < phys.dropBodies.length; i += 1) {
          const o = i * 7;
          dummy.position.set(phys.transforms[o], phys.transforms[o + 1], phys.transforms[o + 2]);
          dummy.quaternion.set(
            phys.transforms[o + 3],
            phys.transforms[o + 4],
            phys.transforms[o + 5],
            phys.transforms[o + 6],
          );
          dummy.scale.set(1, 1, 1);
          dummy.updateMatrix();
          mesh.setMatrixAt(i, dummy.matrix);
        }
        mesh.instanceMatrix.needsUpdate = true;
        mesh.count = phys.dropBodies.length;
      }
    }

    const instFps = delta > 1e-5 ? 1 / delta : fpsEma.current;
    fpsEma.current = fpsEma.current * 0.9 + instFps * 0.1;
    writeState({
      ready: true,
      surface: surfaceIdRef.current,
      face: faceRef.current,
      lighting: lightingRef.current,
      settingsHash: applied.current.hash,
      dropBodies: phys?.dropBodies.length ?? 0,
      dropAwake: phys?.awakeCount ?? 0,
      simTime: phys?.simTime ?? 0,
      fps: fpsEma.current,
      bakedActive: !!bakedHandleRef.current,
      textureCount: textureCount(),
    });
  });

  return (
    <>
      <LightingHost lightingRef={lightingRef} dirtyRef={lightingDirtyRef} />
      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        dampingFactor={0.08}
        target={[0, 2.15, 0]}
        minDistance={1.4}
        maxDistance={24}
        maxPolarAngle={Math.PI * 0.495}
      />
      <CameraDampingCutoff controlsRef={controlsRef} resetRef={cameraSnapResetRef} />
    </>
  );
}

function ControlInput({
  spec,
  value,
  onChange,
}: {
  spec: SurfaceControlSpec;
  value: number | string | boolean;
  onChange: (key: string, value: number | string | boolean) => void;
}) {
  if (spec.kind === 'color') {
    return (
      <label className="tl-row">
        <span>{spec.label}</span>
        <input
          type="color"
          data-testid={`control-${spec.key}`}
          value={typeof value === 'string' ? value : '#ffffff'}
          onChange={(e) => onChange(spec.key, e.target.value)}
        />
      </label>
    );
  }
  if (spec.kind === 'toggle') {
    return (
      <label className="tl-row">
        <span>{spec.label}</span>
        <input
          type="checkbox"
          data-testid={`control-${spec.key}`}
          checked={Boolean(value)}
          onChange={(e) => onChange(spec.key, e.target.checked)}
        />
      </label>
    );
  }
  const num = typeof value === 'number' ? value : Number(spec.default);
  return (
    <label className="tl-row">
      <span>
        {spec.label}
        <b>{Number.isInteger(spec.step) && spec.step === 1 ? num.toFixed(0) : num.toFixed(2)}</b>
      </span>
      <input
        type="range"
        data-testid={`control-${spec.key}`}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        value={num}
        onChange={(e) => onChange(spec.key, Number(e.target.value))}
      />
    </label>
  );
}

export default function TextureLab({ onBack }: { onBack: () => void }) {
  const initialId = useMemo(() => initialSurfaceId(), []);
  const initialDef = getSurface(initialId) ?? LAB_SURFACES[0];
  const surfaceIdRef = useRef<ProfileId>(initialDef.id);
  const faceRef = useRef<SurfaceFace>('exterior');
  const settingsRef = useRef<unknown>(cloneSettings(initialDef.defaultSettings));
  const lightingRef = useRef<LightingKind>('dusk');
  const lightingDirtyRef = useRef(true);
  const dropCmdRef = useRef<'spawn' | 'clear' | null>(null);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const cameraCmdRef = useRef<CameraKind | null>(null);
  const bakePreviewRef = useRef(true);
  const beautyCmdRef = useRef<BeautyCmd>(null);
  const cameraSnapResetRef = useRef(true);

  const [surfaceId, setSurfaceId] = useState<ProfileId>(initialDef.id);
  const [face, setFace] = useState<SurfaceFace>('exterior');
  const [lighting, setLighting] = useState<LightingKind>('dusk');
  const [settings, setSettings] = useState<Record<string, unknown>>(
    () => cloneSettings(initialDef.defaultSettings) as Record<string, unknown>,
  );
  const [hud, setHud] = useState({
    fps: 0,
    dropBodies: 0,
    dropAwake: 0,
    bakePreview: true,
    beautyActive: false,
    beautySamples: 0,
    beautyDone: false,
    bakedActive: true,
    bakeFailed: false,
    bakeMs: 0,
  });

  const definition = getSurface(surfaceId) ?? initialDef;
  const bundle = getSurfaceBundle(surfaceId);

  const applySettings = useCallback(
    (next: unknown) => {
      settingsRef.current = next;
      setSettings(next as Record<string, unknown>);
    },
    [],
  );

  const switchSurface = useCallback(
    (id: ProfileId) => {
      const def = getSurface(id);
      if (!def) return;
      surfaceIdRef.current = id;
      const next = cloneSettings(def.defaultSettings);
      settingsRef.current = next;
      setSurfaceId(id);
      setSettings(next as Record<string, unknown>);
      writeState({ surface: id, settingsHash: hashSettings(next) });
    },
    [],
  );

  const setFaceBoth = useCallback((next: SurfaceFace) => {
    faceRef.current = next;
    setFace(next);
    writeState({ face: next });
  }, []);

  const setLightingBoth = useCallback((next: LightingKind) => {
    lightingRef.current = next;
    lightingDirtyRef.current = true;
    setLighting(next);
    writeState({ lighting: next });
  }, []);

  const onControl = useCallback(
    (key: string, value: number | string | boolean) => {
      const def = getSurface(surfaceIdRef.current);
      if (!def) return;
      const merged = { ...(settingsRef.current as Record<string, unknown>), [key]: value };
      const next = def.settingsFromControls(merged);
      applySettings(next);
    },
    [applySettings],
  );

  const resetSettings = useCallback(() => {
    const def = getSurface(surfaceIdRef.current);
    if (!def) return;
    applySettings(cloneSettings(def.defaultSettings));
  }, [applySettings]);

  const copyJson = useCallback(() => {
    void navigator.clipboard.writeText(JSON.stringify(settingsRef.current, null, 2));
  }, []);

  const downloadJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(settingsRef.current, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${surfaceIdRef.current}-settings.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  useEffect(() => {
    writeState({
      ready: false,
      surface: surfaceIdRef.current,
      face: faceRef.current,
      lighting: lightingRef.current,
      settingsHash: hashSettings(settingsRef.current),
      dropBodies: 0,
      fps: 0,
      dropAwake: 0,
      simTime: 0,
      bakePreview: bakePreviewRef.current,
      bakedActive: false,
      bakeFailed: false,
      bakeMs: 0,
      textureCount: 0,
      bakeSeq: 0,
      beautyShot: { active: false, samples: 0, done: false },
    });
    const timer = window.setInterval(() => {
      const s = window.__textureLabState;
      if (!s) return;
      setHud({
        fps: s.fps,
        dropBodies: s.dropBodies,
        dropAwake: s.dropAwake,
        bakePreview: s.bakePreview,
        beautyActive: s.beautyShot.active,
        beautySamples: s.beautyShot.samples,
        beautyDone: s.beautyShot.done,
        bakedActive: s.bakedActive,
        bakeFailed: s.bakeFailed,
        bakeMs: s.bakeMs,
      });
    }, 250);

    window.__textureLabProbe = {
      setSurface(id) {
        switchSurface(id as ProfileId);
      },
      setFace(next) {
        setFaceBoth(next);
      },
      setLighting(kind) {
        setLightingBoth(kind);
      },
      setCamera(px, py, pz, tx = 0, ty = 2.15, tz = 0, fov) {
        const obj = controlsRef.current?.object;
        if (!obj) return;
        applyCamera(obj, controlsRef.current, {
          pos: [px, py, pz],
          target: [tx, ty, tz],
          fov: fov ?? CAMERAS.mid.fov,
        });
        cameraSnapResetRef.current = true;
      },
      getCamera() {
        const c = controlsRef.current;
        if (!c) return null;
        return {
          x: c.object.position.x,
          y: c.object.position.y,
          z: c.object.position.z,
          tx: c.target.x,
          ty: c.target.y,
          tz: c.target.z,
        };
      },
      drop() {
        dropCmdRef.current = 'spawn';
      },
      clearDrop() {
        dropCmdRef.current = 'clear';
      },
      setControl(key, value) {
        onControl(key, value);
      },
      getSettings() {
        return settingsRef.current;
      },
      resetSettings,
      setBakePreview(on) {
        bakePreviewRef.current = on;
        writeState({ bakePreview: on });
      },
      startBeautyShot(opts) {
        beautyCmdRef.current = {
          kind: 'start',
          samples: opts?.samples,
          height: opts?.height,
          bakeSize: opts?.bakeSize,
          rasterOnly: opts?.rasterOnly,
        };
        writeState({ beautyShot: { active: true, samples: 0, done: false } });
      },
      stopBeautyShot() {
        beautyCmdRef.current = { kind: 'stop' };
      },
      saveBeautyPng() {
        beautyCmdRef.current = { kind: 'save' };
      },
    };

    return () => {
      window.clearInterval(timer);
      delete window.__textureLabProbe;
      delete window.__textureLabState;
    };
  }, [onControl, resetSettings, setFaceBoth, setLightingBoth, switchSurface]);

  const grouped = useMemo(() => {
    const groups: Record<string, SurfaceControlSpec[]> = { base: [], detail: [], wear: [], advanced: [] };
    for (const spec of definition.controls) {
      groups[spec.group].push(spec);
    }
    return groups;
  }, [definition]);

  const physics = bundle?.physics;
  const appearance = bundle?.appearance;

  const labUi = useLabUi();
  useEffect(() => {
    labUiReset();
  }, []);

  return (
    <div className="texture-lab-page" data-testid="texture-lab-page">
      {!labUi.ready && (
        <div className="tl-loading" data-testid="tl-loading">
          <div className="tl-spinner" />
          <div className="tl-loading-text">Baking surfaces…</div>
        </div>
      )}
      {labUi.ready && labUi.busy && (
        <div className="tl-baking-chip" data-testid="tl-baking-chip">
          <div className="tl-spinner tl-spinner-sm" />
          <span>Baking…</span>
        </div>
      )}
      {hud.bakeFailed && (
        <div className="tl-bake-error" data-testid="tl-bake-error" role="alert">
          Surface bake failed
        </div>
      )}
      <div className="tl-stage" data-testid="tl-stage">
        <Canvas
          className="tl-canvas-root"
          shadows
          dpr={1}
          camera={{ position: CAMERAS.wide.pos, fov: CAMERAS.wide.fov, near: 0.08, far: 80 }}
          gl={{
            antialias: true,
            alpha: false,
            toneMapping: THREE.AgXToneMapping,
            toneMappingExposure: EXPOSURE.dusk,
            powerPreference: 'high-performance',
            preserveDrawingBuffer: true,
          }}
          onCreated={({ gl }) => {
            gl.setClearColor('#0a0c10');
            gl.toneMapping = THREE.AgXToneMapping;
            gl.toneMappingExposure = EXPOSURE.dusk;
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
            gl.domElement.style.touchAction = 'none';
          }}
        >
          <LabScene
            surfaceIdRef={surfaceIdRef}
            faceRef={faceRef}
            settingsRef={settingsRef}
            lightingRef={lightingRef}
            lightingDirtyRef={lightingDirtyRef}
            dropCmdRef={dropCmdRef}
            controlsRef={controlsRef}
            cameraCmdRef={cameraCmdRef}
            bakePreviewRef={bakePreviewRef}
            beautyCmdRef={beautyCmdRef}
            cameraSnapResetRef={cameraSnapResetRef}
          />
        </Canvas>
      </div>

      <div className="tl-overlay">
        <div className="tl-brand">
          <button className="tl-exit" type="button" onClick={onBack} data-testid="tl-back">
            ← Lab
          </button>
          <h1 className="tl-title">Texture Lab</h1>
          <p className="tl-sub">Surface look · physics bundle · drop test</p>
        </div>

        <aside className={`tl-panel tl-panel-left${hud.beautyActive ? ' is-frozen' : ''}`} data-testid="tl-controls">
          <h2>Surface</h2>
          <div className="tl-mats">
            {LAB_SURFACES.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`surface-${s.id}`}
                className={s.id === surfaceId ? 'is-on' : ''}
                onClick={() => switchSurface(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <h2>Face</h2>
          <div className="tl-faces">
            <button
              type="button"
              data-testid="face-exterior"
              className={face === 'exterior' ? 'is-on' : ''}
              onClick={() => setFaceBoth('exterior')}
            >
              Exterior
            </button>
            <button
              type="button"
              data-testid="face-interior"
              className={face === 'interior' ? 'is-on' : ''}
              onClick={() => setFaceBoth('interior')}
            >
              Interior
            </button>
          </div>
          <h2>Lighting</h2>
          <select
            className="tl-select"
            data-testid="lighting-select"
            value={lighting}
            onChange={(e) => setLightingBoth(e.target.value as LightingKind)}
          >
            <option value="dusk">Dusk</option>
            <option value="studio">Studio</option>
            <option value="gallery">Gallery</option>
          </select>
          <h2>Camera</h2>
          <div className="tl-cams">
            {(['wide', 'mid', 'close'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                data-testid={`camera-${kind}`}
                disabled={hud.beautyActive}
                onClick={() => {
                  cameraCmdRef.current = kind;
                }}
              >
                {kind}
              </button>
            ))}
          </div>
          <h2>Beauty</h2>
          <label className="tl-row">
            <span>Bake preview</span>
            <input
              type="checkbox"
              data-testid="bake-preview"
              checked={hud.bakePreview}
              disabled={hud.beautyActive}
              onChange={(e) => {
                bakePreviewRef.current = e.target.checked;
                writeState({ bakePreview: e.target.checked });
              }}
            />
          </label>
          <div className="tl-actions" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button
              type="button"
              data-testid="beauty-shot"
              disabled={hud.beautyActive}
              onClick={() => {
                beautyCmdRef.current = { kind: 'start', samples: 512 };
                writeState({ beautyShot: { active: true, samples: 0, done: false } });
              }}
            >
              Beauty shot
            </button>
            <button
              type="button"
              data-testid="beauty-stop"
              disabled={!hud.beautyActive}
              onClick={() => {
                beautyCmdRef.current = { kind: 'stop' };
              }}
            >
              Stop
            </button>
          </div>
          <button
            type="button"
            data-testid="beauty-save"
            disabled={!hud.beautyActive}
            onClick={() => {
              beautyCmdRef.current = { kind: 'save' };
            }}
          >
            Save PNG
          </button>
          {(['base', 'detail', 'wear', 'advanced'] as const).map((group) =>
            grouped[group].length === 0 ? null : (
              <div key={group}>
                <div className="tl-group">{group}</div>
                {grouped[group].map((spec) => (
                  <ControlInput
                    key={spec.key}
                    spec={spec}
                    value={settings[spec.key] as number | string | boolean}
                    onChange={onControl}
                  />
                ))}
              </div>
            ),
          )}
          <div className="tl-actions">
            <button type="button" data-testid="reset-settings" onClick={resetSettings}>
              Reset
            </button>
            <button type="button" data-testid="copy-json" onClick={copyJson}>
              Copy JSON
            </button>
            <button type="button" data-testid="download-json" onClick={downloadJson}>
              Download
            </button>
          </div>
          <div className="tl-actions" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button type="button" data-testid="drop-test" onClick={() => { dropCmdRef.current = 'spawn'; }}>
              Drop test
            </button>
            <button type="button" data-testid="drop-clear" onClick={() => { dropCmdRef.current = 'clear'; }}>
              Clear
            </button>
          </div>
        </aside>

        <aside className="tl-panel tl-panel-right" data-testid="tl-bundle">
          <h2>Physics</h2>
          {physics && (
            <>
              <p className="tl-readout">density {physics.density} kg/m³</p>
              <p className="tl-readout">friction {physics.surface.friction}</p>
              <p className="tl-readout">restitution {physics.surface.restitution}</p>
              <p className="tl-readout">
                strength {physics.physics.strength.compressive} / {physics.physics.strength.tensile} /{' '}
                {physics.physics.strength.shear} MPa
              </p>
              <p className="tl-readout">fracture {physics.seedSpec.kind}</p>
            </>
          )}
          <h2>VFX appearance</h2>
          {appearance && (
            <div className="tl-swatches">
              <div className="tl-swatch">
                <span className="tl-chip" style={{ background: appearance.dust.color }} />
                dust
              </div>
              <div className="tl-swatch">
                <span className="tl-chip" style={{ background: appearance.chips.color }} />
                chips
              </div>
              <div className="tl-swatch">
                <span className="tl-chip" style={{ background: appearance.debrisTint }} />
                debris
              </div>
              <div className="tl-swatch">
                <span className="tl-chip" style={{ background: appearance.fragment.outer }} />
                outer
              </div>
              <div className="tl-swatch">
                <span className="tl-chip" style={{ background: appearance.fragment.interior }} />
                interior
              </div>
            </div>
          )}
        </aside>

        <div className="tl-hud" data-testid="tl-hud">
          fps <b>{hud.fps.toFixed(0)}</b> · drops <b>{hud.dropBodies}</b> · awake <b>{hud.dropAwake}</b>
          {hud.bakedActive ? (
            <>
              {' '}
              · baked <b>{hud.bakeMs.toFixed(0)}ms</b>
            </>
          ) : hud.bakeFailed ? (
            ' · bake failed'
          ) : null}
          {hud.beautyActive ? (
            <>
              {' '}
              · beauty <b data-testid="beauty-samples">{hud.beautySamples.toFixed(0)}</b>
              {hud.beautyDone ? ' · done' : ''}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
